import { z } from 'zod';

export const RoleSchema = z.enum(['owner', 'manager', 'employee']);
export type Role = z.infer<typeof RoleSchema>;

export const PaymentMethodSchema = z.enum([
  'cash',
  'card',
  'upi',
  'bank_transfer',
  'other',
]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const StoreSchema = z.object({
  name: z.string().min(1, 'Store name is required'),
  address: z.string().optional().default(''),
  phone: z.string().optional().default(''),
  isActive: z.boolean().default(true),
});
export type Store = z.infer<typeof StoreSchema> & {
  id: string;
  createdAt?: number;
  updatedAt?: number;
};

export const EmployeeSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional().default(''),
  role: RoleSchema,
  storeIds: z.array(z.string()).default([]),
  uid: z.string().optional(),
  isActive: z.boolean().default(true),
});
export type Employee = z.infer<typeof EmployeeSchema> & {
  id: string;
  createdAt?: number;
};

// ----- Inventory: rich categories & ledger model -----
export const AttributeDefinitionSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(['text', 'number', 'select']),
  options: z.array(z.string()).optional(),
  required: z.boolean().default(false),
});
export type AttributeDefinition = z.infer<typeof AttributeDefinitionSchema>;

export const CategorySchema = z.object({
  name: z.string().min(1, 'Category name is required'),
  codePrefix: z
    .string()
    .min(2, 'Prefix must be at least 2 characters')
    .max(5, 'Prefix must be at most 5 characters')
    .regex(/^[A-Z]+$/, 'Use uppercase letters only'),
  attributeSchema: z.array(AttributeDefinitionSchema).default([]),
  lowStockThresholdDefault: z.coerce.number().int().nonnegative().default(5),
  hasExpiry: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type Category = z.infer<typeof CategorySchema> & {
  id: string;
  archivedAt?: number | null;
  archivedBy?: string | null;
  createdAt?: number;
  updatedAt?: number;
};

export const BrandSchema = z.object({
  name: z.string().min(1, 'Brand name is required'),
  categoryId: z.string().min(1, 'Category is required'),
});
export type Brand = z.infer<typeof BrandSchema> & {
  id: string;
  createdAt?: number;
};

export const StockMovementTypeSchema = z.enum([
  'RESTOCK_IN',
  'MANUAL_OUT',
  'POS_SALE',
  'ADJUSTMENT',
]);
export type StockMovementType = z.infer<typeof StockMovementTypeSchema>;

export const StockMovementSchema = z.object({
  itemId: z.string().min(1),
  storeId: z.string().min(1),
  type: StockMovementTypeSchema,
  quantity: z.coerce.number().int().positive('Quantity must be at least 1'),
  note: z.string().min(1, 'Note is required'),
  supplierId: z.string().optional().or(z.literal('')),
  referenceId: z.string().optional().or(z.literal('')),
  serialNumbers: z.array(z.string()).optional(),
});
export type StockMovement = z.infer<typeof StockMovementSchema> & {
  id: string;
  actorId?: string;
  applied?: boolean;
  createdAt?: number;
  appliedAt?: number;
};

export const SupplierSchema = z.object({
  name: z.string().min(1, 'Supplier name is required'),
  contactPhone: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  totalAmount: z.coerce.number().nonnegative().default(0),
  advanceAmount: z.coerce.number().nonnegative().default(0),
  isActive: z.boolean().default(true),
});
export type Supplier = z.infer<typeof SupplierSchema> & {
  id: string;
  archivedAt?: number | null;
  archivedBy?: string | null;
  createdAt?: number;
  updatedAt?: number;
};

export const SupplierPurchaseEntrySchema = z.object({
  supplierId: z.string().min(1),
  type: z.enum(['PURCHASE', 'ADVANCE']),
  amount: z.coerce.number().positive('Amount must be positive'),
  description: z.string().min(1, 'Description is required'),
});
export type SupplierPurchaseEntry = z.infer<typeof SupplierPurchaseEntrySchema> & {
  id: string;
  actorId?: string;
  createdAt?: number;
};

export const SerializedItemSchema = z.object({
  itemId: z.string().min(1),
  storeId: z.string().min(1),
  serialNumber: z.string().min(1),
  isAvailable: z.boolean().default(true),
});
export type SerializedItem = z.infer<typeof SerializedItemSchema> & {
  id: string;
  createdAt?: number;
  updatedAt?: number;
};

export const InventoryItemSchema = z.object({
  itemName: z.string().min(1, 'Product name is required'),
  categoryId: z.string().optional().or(z.literal('')),
  category: z.string().min(1, 'Category is required'),
  brandId: z.string().optional().or(z.literal('')),
  brand: z.string().min(1, 'Brand is required'),
  subBrand: z.string().optional().default(''),
  variant: z.string().optional().default(''),
  sku: z.string().optional().default(''),
  unit: z.string().optional().default('piece'),
  length: z.string().optional().default(''),
  width: z.string().optional().default(''),
  thickness: z.string().optional().default(''),
  attributeValues: z.record(z.unknown()).default({}),
  purchasePrice: z.coerce.number().nonnegative().default(0),
  sellingPrice: z.coerce.number().nonnegative().default(0),
  quantity: z.coerce.number().int().nonnegative().default(0),
  lowStockThreshold: z.coerce.number().int().nonnegative().default(5),
  expiryDate: z.string().optional().or(z.literal('')),
  imageUrl: z.string().optional().or(z.literal('')),
  supplierId: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  source: z.enum(['manual', 'excel_import']).default('manual'),
  isActive: z.boolean().default(true),
});
export type InventoryItem = z.infer<typeof InventoryItemSchema> & {
  id: string;
  lowStock?: boolean;
  createdAt?: number;
  updatedAt?: number;
  createdBy?: string;
  archivedAt?: number | null;
  archivedBy?: string | null;
};

/** Owner and manager can view purchase/cost prices */
export function canViewCost(role: Role | null): boolean {
  return role === 'owner' || role === 'manager';
}

export function formatSize(item: { variant?: string; length?: string; width?: string; thickness?: string }): string {
  if (item.variant?.trim()) return item.variant.trim();
  const l = item.length?.trim();
  const w = item.width?.trim();
  const t = item.thickness?.trim();
  if (!l && !w && !t) return '—';
  if (l && w && t) return `${l}x${w} x ${t}`;
  if (l && w) return `${l}x${w}`;
  if (t) return t;
  return [l, w, t].filter(Boolean).join(' x ');
}

export const CustomerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  phone: z
    .string()
    .min(7, 'Phone must be at least 7 digits')
    .regex(/^[+0-9 \-()]+$/, 'Invalid phone format'),
  email: z.string().email().optional().or(z.literal('')),
  address: z.string().optional().default(''),
  source: z.enum(['manual', 'excel_import']).default('manual'),
});
export type Customer = z.infer<typeof CustomerSchema> & {
  id: string;
  totalLifetimeValue?: number;
  orderCount?: number;
  createdAt?: number;
};

export const OrderItemSchema = z.object({
  itemId: z.string(),
  itemName: z.string(),
  sku: z.string().optional().default(''),
  price: z.coerce.number().nonnegative(),
  quantity: z.coerce.number().int().positive(),
  total: z.coerce.number().nonnegative(),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

export const QuotationItemSchema = OrderItemSchema.extend({
  quantity: z.coerce.number().positive(),
  per: z.string().optional().default('Piece'),
});
export type QuotationItem = z.infer<typeof QuotationItemSchema>;

export const OrderSchema = z.object({
  customerId: z.string(),
  customerSnapshot: z.object({
    name: z.string(),
    phone: z.string(),
  }),
  items: z.array(OrderItemSchema).min(1),
  subtotal: z.coerce.number().nonnegative(),
  deliveryCharge: z.coerce.number().nonnegative().default(0),
  discount: z.coerce.number().nonnegative().default(0),
  totalAmount: z.coerce.number().nonnegative(),
  paymentMethod: PaymentMethodSchema,
  storeId: z.string(),
  notes: z.string().optional().default(''),
});
export type Order = z.infer<typeof OrderSchema> & {
  id: string;
  createdAt?: number;
  createdBy?: string;
  invoiceNumber?: string;
  invoicePdfUrl?: string;
  stockDeducted?: boolean;
};

export const QuotationSchema = z.object({
  customerId: z.string().optional(),
  customerSnapshot: z
    .object({
      name: z.string(),
      phone: z.string().optional().default(''),
      address: z.string().optional().default(''),
    })
    .optional(),
  items: z.array(QuotationItemSchema).default([]),
  subtotal: z.coerce.number().nonnegative().default(0),
  deliveryCharge: z.coerce.number().nonnegative().default(0),
  discount: z.coerce.number().nonnegative().default(0),
  totalAmount: z.coerce.number().nonnegative().default(0),
  status: z.enum(['draft', 'final']).default('draft'),
  notes: z.string().optional().default(''),
});
export type Quotation = z.infer<typeof QuotationSchema> & {
  id: string;
  shareToken?: string;
  pdfUrl?: string;
  quotationNumber?: string;
  createdAt?: number;
  updatedAt?: number;
  createdBy?: string;
};

export const SettingsSchema = z.object({
  businessName: z.string().min(1).default('CHHABIGHAR'),
  branding: z
    .object({
      logoUrl: z.string().optional().default(''),
      themeColor: z.string().optional().default('#C9A441'),
      tagline: z.string().optional().default(''),
    })
    .default({ logoUrl: '', themeColor: '#C9A441', tagline: '' }),
  gstNumber: z.string().optional().default(''),
  invoicePrefix: z.string().default('INV-'),
  quotationPrefix: z.string().default('QT-'),
  taxRate: z.coerce.number().min(0).max(100).default(0),
  address: z.string().optional().default(''),
  phone: z.string().optional().default(''),
  email: z.string().optional().default(''),
});
export type Settings = z.infer<typeof SettingsSchema>;

export type Notification = {
  id: string;
  type: 'low_stock' | 'order_created' | 'system';
  title: string;
  body: string;
  itemId?: string;
  orderId?: string;
  read: boolean;
  createdAt: number;
};
